/**
 * BaselineCalculator - Calculates typical patterns from historical data
 *
 * Layer 3: Aggregation & Query
 *
 * Purpose:
 * - Calculate rolling averages (7-day, 28-day)
 * - Identify day-of-week patterns (Mondays vs Fridays)
 * - Compute statistical baselines (mean, std deviation, min, max)
 * - Store pre-calculated baselines for fast dashboard loads
 *
 * Why This Matters:
 * Without baselines:
 * - Dashboard must read 28 documents to calculate "today vs average"
 * - No context for whether today is unusual
 * - No pattern detection
 *
 * With baselines:
 * - Dashboard reads 1 document for complete context
 * - "Today: 15 mentions (+40% vs 7-day average of 10.7)"
 * - Automatic anomaly detection
 *
 * Performance:
 * - Baselines update once per day (off-peak hours)
 * - Dashboard reads cached baselines (1 Firestore read)
 * - Reduces 28 reads → 1 read (96% improvement)
 *
 * Integration:
 * - Called by scheduled job (daily at midnight)
 * - Reads from Layer 2 (StorageService)
 * - Writes to stats/baselines collection
 */

import { doc, setDoc, getDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../../../firebase';

export class BaselineCalculator {
  constructor(storageService) {
    if (!storageService) {
      throw new Error('BaselineCalculator requires a storage service');
    }
    this.storage = storageService;
    this.baselinesPath = 'baselines';
  }

  /**
   * Calculate and store 7-day rolling baseline
   * @returns {Promise<Object>} Calculated baseline
   */
  async calculate7DayBaseline() {
    try {
      this.log('Calculating 7-day baseline...');

      // Fetch last 7 days of stats
      const recentStats = await this.storage.getRecentDailyStats(7);

      if (recentStats.length === 0) {
        this.log('No data for 7-day baseline');
        return this.getEmptyBaseline();
      }

      // Calculate metrics
      const baseline = {
        // Time window
        period: '7-day',
        daysIncluded: recentStats.length,
        startDate: recentStats[recentStats.length - 1]?.meta?.dateKey,
        endDate: recentStats[0]?.meta?.dateKey,

        // Overall metrics
        averageMentionsPerDay: this.calculateAverage(recentStats, 'totals.mentions'),
        averageConsequencesPerDay: this.calculateAverage(recentStats, 'totals.consequences'),
        averageUniqueStudents: this.calculateAverage(recentStats, 'totals.uniqueStudents'),

        // Statistical measures
        stdDeviationMentions: this.calculateStdDeviation(recentStats, 'totals.mentions'),
        minMentions: this.calculateMin(recentStats, 'totals.mentions'),
        maxMentions: this.calculateMax(recentStats, 'totals.mentions'),

        // Hourly patterns (average mentions per hour across all days)
        averageByHour: this.calculateHourlyAverages(recentStats),

        // Severity distribution (percentage)
        severityDistribution: this.calculateSeverityDistribution(recentStats),

        // Period patterns
        periodDistribution: this.calculatePeriodDistribution(recentStats),

        // Metadata
        calculatedAt: Timestamp.now(),
        lastUpdated: Timestamp.now()
      };

      // Store baseline
      const docRef = doc(db, this.baselinesPath, 'rolling7day');
      await setDoc(docRef, baseline);

      this.log('7-day baseline calculated', { avgMentions: baseline.averageMentionsPerDay });
      return baseline;
    } catch (error) {
      console.error('BaselineCalculator: Failed to calculate 7-day baseline:', error);
      throw error;
    }
  }

  /**
   * Calculate and store 28-day rolling baseline
   * @returns {Promise<Object>} Calculated baseline
   */
  async calculate28DayBaseline() {
    try {
      this.log('Calculating 28-day baseline...');

      // Fetch last 28 days of stats
      const recentStats = await this.storage.getRecentDailyStats(28);

      if (recentStats.length === 0) {
        this.log('No data for 28-day baseline');
        return this.getEmptyBaseline();
      }

      // Calculate day-of-week patterns
      const dayOfWeekAverages = this.calculateDayOfWeekAverages(recentStats);

      const baseline = {
        // Time window
        period: '28-day',
        daysIncluded: recentStats.length,
        startDate: recentStats[recentStats.length - 1]?.meta?.dateKey,
        endDate: recentStats[0]?.meta?.dateKey,

        // Overall metrics
        averageMentionsPerDay: this.calculateAverage(recentStats, 'totals.mentions'),
        averageConsequencesPerDay: this.calculateAverage(recentStats, 'totals.consequences'),
        averageUniqueStudents: this.calculateAverage(recentStats, 'totals.uniqueStudents'),

        // Statistical measures
        stdDeviationMentions: this.calculateStdDeviation(recentStats, 'totals.mentions'),
        minMentions: this.calculateMin(recentStats, 'totals.mentions'),
        maxMentions: this.calculateMax(recentStats, 'totals.mentions'),

        // Day-of-week patterns
        averageByDayOfWeek: dayOfWeekAverages,

        // Hourly patterns
        averageByHour: this.calculateHourlyAverages(recentStats),

        // Top offenses (most common)
        topOffenses: this.calculateTopOffenses(recentStats, 5),

        // Auto-generation rate
        autoGenerationRate: this.calculateAutoGenerationRate(recentStats),

        // Escalation rate
        escalationRate: this.calculateEscalationRate(recentStats),

        // Metadata
        calculatedAt: Timestamp.now(),
        lastUpdated: Timestamp.now()
      };

      // Store baseline
      const docRef = doc(db, this.baselinesPath, 'rolling28day');
      await setDoc(docRef, baseline);

      this.log('28-day baseline calculated', { avgMentions: baseline.averageMentionsPerDay });
      return baseline;
    } catch (error) {
      console.error('BaselineCalculator: Failed to calculate 28-day baseline:', error);
      throw error;
    }
  }

  /**
   * Get stored baseline
   * @param {string} period - '7-day' or '28-day'
   * @returns {Promise<Object>}
   */
  async getBaseline(period = '28-day') {
    try {
      const docId = period === '7-day' ? 'rolling7day' : 'rolling28day';
      const docRef = doc(db, this.baselinesPath, docId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        this.log(`No ${period} baseline found, calculating...`);
        // Calculate if not exists
        return period === '7-day'
          ? await this.calculate7DayBaseline()
          : await this.calculate28DayBaseline();
      }

      return {
        id: docSnap.id,
        ...docSnap.data()
      };
    } catch (error) {
      console.error('BaselineCalculator: Failed to get baseline:', error);
      return this.getEmptyBaseline();
    }
  }

  /**
   * Calculate average for a specific field across all stats
   * @param {Array} stats - Array of daily stats
   * @param {string} field - Field path (e.g., 'totals.mentions')
   * @returns {number}
   */
  calculateAverage(stats, field) {
    if (!stats || stats.length === 0) return 0;

    const values = stats.map(s => this.getNestedValue(s, field) || 0);
    const sum = values.reduce((a, b) => a + b, 0);
    return Math.round((sum / values.length) * 10) / 10; // Round to 1 decimal
  }

  /**
   * Calculate standard deviation for a field
   * @param {Array} stats - Array of daily stats
   * @param {string} field - Field path
   * @returns {number}
   */
  calculateStdDeviation(stats, field) {
    if (!stats || stats.length === 0) return 0;

    const values = stats.map(s => this.getNestedValue(s, field) || 0);
    const avg = values.reduce((a, b) => a + b, 0) / values.length;
    const squareDiffs = values.map(value => Math.pow(value - avg, 2));
    const avgSquareDiff = squareDiffs.reduce((a, b) => a + b, 0) / values.length;

    return Math.round(Math.sqrt(avgSquareDiff) * 10) / 10; // Round to 1 decimal
  }

  /**
   * Calculate minimum value for a field
   * @param {Array} stats - Array of daily stats
   * @param {string} field - Field path
   * @returns {number}
   */
  calculateMin(stats, field) {
    if (!stats || stats.length === 0) return 0;
    const values = stats.map(s => this.getNestedValue(s, field) || 0);
    return Math.min(...values);
  }

  /**
   * Calculate maximum value for a field
   * @param {Array} stats - Array of daily stats
   * @param {string} field - Field path
   * @returns {number}
   */
  calculateMax(stats, field) {
    if (!stats || stats.length === 0) return 0;
    const values = stats.map(s => this.getNestedValue(s, field) || 0);
    return Math.max(...values);
  }

  /**
   * Calculate average mentions per hour across all days
   * @param {Array} stats - Array of daily stats
   * @returns {Object} { 0: avg, 1: avg, ..., 23: avg }
   */
  calculateHourlyAverages(stats) {
    if (!stats || stats.length === 0) return {};

    const hourlyTotals = {};
    for (let hour = 0; hour < 24; hour++) {
      hourlyTotals[hour] = 0;
    }

    // Sum up all hours across all days
    stats.forEach(dayStat => {
      if (dayStat.hourly) {
        Object.entries(dayStat.hourly).forEach(([hour, count]) => {
          hourlyTotals[hour] = (hourlyTotals[hour] || 0) + (count || 0);
        });
      }
    });

    // Calculate averages
    const hourlyAverages = {};
    Object.entries(hourlyTotals).forEach(([hour, total]) => {
      hourlyAverages[hour] = Math.round((total / stats.length) * 10) / 10;
    });

    return hourlyAverages;
  }

  /**
   * Calculate day-of-week averages
   * @param {Array} stats - Array of daily stats
   * @returns {Object} { 0: avg, 1: avg, ..., 6: avg } (0=Sunday)
   */
  calculateDayOfWeekAverages(stats) {
    if (!stats || stats.length === 0) return {};

    const dayTotals = { 0: [], 1: [], 2: [], 3: [], 4: [], 5: [], 6: [] };

    // Group stats by day of week
    stats.forEach(dayStat => {
      if (dayStat.meta?.date) {
        const date = dayStat.meta.date.toDate ? dayStat.meta.date.toDate() : new Date(dayStat.meta.date);
        const dayOfWeek = date.getDay();
        dayTotals[dayOfWeek].push(dayStat.totals?.mentions || 0);
      }
    });

    // Calculate averages for each day
    const dayAverages = {};
    Object.entries(dayTotals).forEach(([day, values]) => {
      if (values.length > 0) {
        const avg = values.reduce((a, b) => a + b, 0) / values.length;
        dayAverages[day] = Math.round(avg * 10) / 10;
      } else {
        dayAverages[day] = 0;
      }
    });

    return dayAverages;
  }

  /**
   * Calculate severity distribution as percentages
   * @param {Array} stats - Array of daily stats
   * @returns {Object}
   */
  calculateSeverityDistribution(stats) {
    if (!stats || stats.length === 0) return { low: 0, medium: 0, high: 0 };

    const totals = { low: 0, medium: 0, high: 0 };

    stats.forEach(dayStat => {
      if (dayStat.bySeverity) {
        totals.low += dayStat.bySeverity.low || 0;
        totals.medium += dayStat.bySeverity.medium || 0;
        totals.high += dayStat.bySeverity.high || 0;
      }
    });

    const sum = totals.low + totals.medium + totals.high;
    if (sum === 0) return { low: 0, medium: 0, high: 0 };

    return {
      low: Math.round((totals.low / sum) * 100),
      medium: Math.round((totals.medium / sum) * 100),
      high: Math.round((totals.high / sum) * 100)
    };
  }

  /**
   * Calculate period distribution
   * @param {Array} stats - Array of daily stats
   * @returns {Object}
   */
  calculatePeriodDistribution(stats) {
    if (!stats || stats.length === 0) return {};

    const totals = {};
    stats.forEach(dayStat => {
      if (dayStat.byPeriod) {
        Object.entries(dayStat.byPeriod).forEach(([period, data]) => {
          totals[period] = (totals[period] || 0) + (data.mentions || 0);
        });
      }
    });

    return totals;
  }

  /**
   * Calculate top offenses across period
   * @param {Array} stats - Array of daily stats
   * @param {number} limit - Number of top offenses to return
   * @returns {Array}
   */
  calculateTopOffenses(stats, limit = 5) {
    if (!stats || stats.length === 0) return [];

    const offenseTotals = {};

    stats.forEach(dayStat => {
      if (dayStat.byOffense) {
        Object.entries(dayStat.byOffense).forEach(([offense, data]) => {
          if (!offenseTotals[offense]) {
            offenseTotals[offense] = {
              name: data.originalName || offense,
              count: 0,
              severity: data.severity
            };
          }
          offenseTotals[offense].count += data.count || 0;
        });
      }
    });

    return Object.values(offenseTotals)
      .sort((a, b) => b.count - a.count)
      .slice(0, limit);
  }

  /**
   * Calculate auto-generation rate (percentage of consequences auto-generated)
   * @param {Array} stats - Array of daily stats
   * @returns {number}
   */
  calculateAutoGenerationRate(stats) {
    if (!stats || stats.length === 0) return 0;

    let totalConsequences = 0;
    let autoGenerated = 0;

    stats.forEach(dayStat => {
      totalConsequences += dayStat.totals?.consequences || 0;
      autoGenerated += dayStat.totals?.autoGeneratedConsequences || 0;
    });

    if (totalConsequences === 0) return 0;
    return Math.round((autoGenerated / totalConsequences) * 100);
  }

  /**
   * Calculate escalation rate (percentage of consequences escalated)
   * @param {Array} stats - Array of daily stats
   * @returns {number}
   */
  calculateEscalationRate(stats) {
    if (!stats || stats.length === 0) return 0;

    let totalConsequences = 0;
    let escalated = 0;

    stats.forEach(dayStat => {
      totalConsequences += dayStat.totals?.consequences || 0;
      escalated += dayStat.totals?.escalatedConsequences || 0;
    });

    if (totalConsequences === 0) return 0;
    return Math.round((escalated / totalConsequences) * 100);
  }

  /**
   * Get nested value from object using dot notation
   * @param {Object} obj - Object
   * @param {string} path - Path (e.g., 'totals.mentions')
   * @returns {*}
   */
  getNestedValue(obj, path) {
    return path.split('.').reduce((acc, part) => acc && acc[part], obj);
  }

  /**
   * Get empty baseline structure
   * @returns {Object}
   */
  getEmptyBaseline() {
    return {
      period: 'unknown',
      daysIncluded: 0,
      averageMentionsPerDay: 0,
      averageConsequencesPerDay: 0,
      averageUniqueStudents: 0,
      stdDeviationMentions: 0,
      minMentions: 0,
      maxMentions: 0,
      calculatedAt: Timestamp.now()
    };
  }

  /**
   * Log calculator activity
   * @param {string} message - Log message
   * @param {Object} data - Additional data
   */
  log(message, data = {}) {
    if (process.env.NODE_ENV === 'development') {
      console.log(`[BaselineCalculator] ${message}`, data);
    }
  }
}
